import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCcw, Ticket, Undo2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { useToast } from '../../hooks/use-toast';
import api from '../../api/client';

const STATUS_LABEL = {
  scheduled: 'Programmata',
  waiting_tracks: 'In avvio',
  live: 'In diretta',
  ended: 'Conclusa',
  cancelled: 'Annullata',
  cancelled_creator_no_show: 'Annullata · no-show',
  cancelled_no_tracks: 'Annullata · nessuna trasmissione',
  cancelled_publish_failed: 'Annullata · errore tecnico',
  cancelled_technical_failure: 'Annullata · errore tecnico',
};

/**
 * Admin — Live con biglietti venduti (ultimi 30 gg) + rimborso manuale.
 * Serve per i casi in cui il rimborso automatico non è scattato
 * (es. live mai avvenuta): un click restituisce i crediti ai compratori
 * e storna il netto dal wallet del creator.
 */
const AdminLiveRefundsPanel = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [refunding, setRefunding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/lives/ticketed', { params: { days: 30 } });
      setItems(data.items || []);
    } catch (e) {
      toast({
        title: 'Errore caricamento biglietti live',
        description: e?.response?.data?.detail || String(e),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const doRefund = async () => {
    if (!target) return;
    setRefunding(true);
    try {
      const { data } = await api.post(`/admin/lives/${target.live_id}/refund`);
      toast({
        title: 'Rimborso completato',
        description: `${data.tickets_refunded} biglietti · ${data.credits_refunded} crediti restituiti ai compratori.`,
      });
      setTarget(null);
      await load();
    } catch (e) {
      toast({
        title: 'Rimborso non riuscito',
        description: e?.response?.data?.detail || 'Riprova.',
        variant: 'destructive',
      });
    } finally {
      setRefunding(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card" data-testid="admin-live-refunds-panel">
      <header className="flex items-center justify-between border-b border-border px-5 py-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <Ticket className="h-4 w-4 gold-text" /> Biglietti Live venduti (30 gg)
          </h2>
          <p className="text-[11px] text-muted-foreground">
            Se una live non si è svolta e il rimborso automatico non è scattato, puoi rimborsare
            manualmente: i compratori riavranno i crediti e il netto verrà stornato alla Voce Pro.
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="live-refunds-reload">
          <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </header>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin gold-text" /></div>
      ) : items.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-muted-foreground" data-testid="live-refunds-empty">
          Nessun biglietto live venduto negli ultimi 30 giorni.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Live</th>
                <th className="px-4 py-2 text-left">Voce Pro</th>
                <th className="px-4 py-2 text-left">Stato</th>
                <th className="px-4 py-2 text-right">Biglietti</th>
                <th className="px-4 py-2 text-right">Crediti</th>
                <th className="px-4 py-2 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((it) => {
                const allRefunded = it.tickets_completed === 0;
                return (
                  <tr key={it.live_id} data-testid={`live-refund-row-${it.live_id}`}>
                    <td className="max-w-[220px] px-4 py-2.5">
                      <p className="truncate font-medium">{it.title || '(senza titolo)'}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {it.went_live ? 'Trasmessa' : 'MAI andata in onda'}
                        {it.last_purchase_at ? ` · ultima vendita ${new Date(it.last_purchase_at).toLocaleDateString()}` : ''}
                      </p>
                    </td>
                    <td className="px-4 py-2.5 text-xs">@{it.creator_username || '—'}</td>
                    <td className="px-4 py-2.5">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                        it.live_status?.startsWith('cancelled')
                          ? 'bg-red-500/15 text-red-300'
                          : it.live_status === 'ended'
                            ? 'bg-white/10 text-neutral-300'
                            : 'bg-emerald-500/15 text-emerald-300'
                      }`}>
                        {STATUS_LABEL[it.live_status] || it.live_status || 'sconosciuto'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-xs tabular-nums">
                      {it.tickets_completed} attivi{it.tickets_refunded > 0 ? ` · ${it.tickets_refunded} rimborsati` : ''}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs">{it.credits}</td>
                    <td className="px-4 py-2.5 text-right">
                      {allRefunded ? (
                        <span className="text-[11px] text-emerald-400" data-testid={`live-refund-done-${it.live_id}`}>Rimborsata</span>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setTarget(it)}
                          className="h-8 border-red-500/40 text-red-400 hover:bg-red-500/10"
                          data-testid={`live-refund-btn-${it.live_id}`}
                        >
                          <Undo2 className="mr-1 h-3.5 w-3.5" /> Rimborsa
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <AlertDialogContent data-testid="live-refund-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Rimborsare i biglietti di questa live?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{target?.title}&quot; di @{target?.creator_username}: {target?.tickets_completed} biglietti
              attivi per {target?.credits} crediti totali. I compratori riceveranno il rimborso completo
              e il netto verrà stornato dal wallet del creator. Se la live è ancora attiva verrà annullata.
              L&apos;operazione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={refunding} data-testid="live-refund-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doRefund(); }}
              disabled={refunding}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="live-refund-confirm-btn"
            >
              {refunding ? 'Rimborso…' : 'Rimborsa tutto'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminLiveRefundsPanel;
